import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  AskInvestmentAssistantRequestBody,
  InvestmentAssistantActionDTO,
  InvestmentAssistantMessageDTO,
  InvestmentAssistantReplyDTO,
} from '@inversor-hub/types';
import { useAskInvestmentAssistant } from '@/lib/api/investments';
import { useGetMe } from '@/lib/api/me.ts';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import {
  ANALYTICS_PLATFORM,
  inferAIUseCase,
  trackProductEvent,
} from '@/lib/analytics/product-events';

interface InvestmentAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
  investmentId: string;
  investmentAddress: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: InvestmentAssistantActionDTO[];
}

const DEFAULT_PROMPTS = [
  'Resume el estado financiero actual de esta inversión',
  'Detecta riesgos y alertas prioritarias',
  'Qué mejoras concretas puedo aplicar este mes',
];

function buildHistory(messages: ChatMessage[]): InvestmentAssistantMessageDTO[] {
  return messages.slice(-8).map((message) => ({
    role: message.role,
    content: message.content,
  }));
}

function priorityColorKey(
  priority: InvestmentAssistantActionDTO['priority']
): 'error' | 'warning' | 'success' {
  if (priority === 'HIGH') return 'error';
  if (priority === 'MEDIUM') return 'warning';
  return 'success';
}

function priorityLabelEs(priority: InvestmentAssistantActionDTO['priority']) {
  if (priority === 'HIGH') return 'Alta';
  if (priority === 'MEDIUM') return 'Media';
  return 'Baja';
}

function mapReplyToChatMessage(reply: InvestmentAssistantReplyDTO): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role: 'assistant',
    content: reply.answer,
    actions: reply.actions,
  };
}

export const InvestmentAssistantDrawer = ({
  open,
  onClose,
  investmentId,
  investmentAddress,
}: InvestmentAssistantDrawerProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draftMessage, setDraftMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const askAssistant = useAskInvestmentAssistant(investmentId);
  const { data: currentUser } = useGetMe();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (open) {
      setTimeout(() => textFieldRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, askAssistant.isPending]);

  const sendMessage = async (messageContent: string) => {
    const normalizedMessage = messageContent.trim();
    if (!normalizedMessage || askAssistant.isPending) return;

    setErrorMessage(null);
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: normalizedMessage,
    };

    const history = buildHistory(messages);
    setMessages((current) => [...current, userMessage]);
    setDraftMessage('');

    if (currentUser) {
      trackProductEvent(trackEvent, 'ai_message_sent', {
        entry_point: 'investment',
        investment_id: investmentId,
        use_case: inferAIUseCase(normalizedMessage),
        role: currentUser.role,
        platform: ANALYTICS_PLATFORM,
      });
    }

    const payload: AskInvestmentAssistantRequestBody = {
      message: normalizedMessage,
      history,
    };

    try {
      const reply = await askAssistant.mutateAsync(payload);
      const assistantMessage = mapReplyToChatMessage(reply);
      setMessages((current) => [...current, assistantMessage]);
    } catch {
      setErrorMessage('No se pudo obtener respuesta del asistente. Inténtalo de nuevo.');
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault();
      sendMessage(draftMessage);
    }
  };

  const isEmpty = messages.length === 0 && !askAssistant.isPending;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AutoAwesomeIcon sx={{ color: 'white', fontSize: 22 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="white" lineHeight={1.2}>
              Asistente IA
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {investmentAddress}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', mt: -0.5 }} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Chat Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {isEmpty ? (
          /* Empty State */
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
            py={4}
          >
            <PsychologyIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                ¿En qué te ayudo?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Consulta transacciones, estado operativo y oportunidades de mejora con contexto
                completo.
              </Typography>
            </Box>
            <Stack spacing={1} width="100%" mt={1}>
              {DEFAULT_PROMPTS.map((prompt) => (
                <Box
                  key={prompt}
                  onClick={() => sendMessage(prompt)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {prompt}
                  </Typography>
                  <ArrowForwardIosIcon sx={{ fontSize: 12, color: 'text.secondary', ml: 1 }} />
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          /* Message Thread */
          <>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                {/* Message Bubble */}
                <Box
                  sx={{
                    maxWidth: message.role === 'user' ? '80%' : '85%',
                    px: 2,
                    py: 1.5,
                    borderRadius:
                      message.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                    color: message.role === 'user' ? 'white' : 'text.primary',
                    border: message.role === 'assistant' ? '1px solid' : 'none',
                    borderColor: 'grey.200',
                    boxShadow:
                      message.role === 'assistant'
                        ? '0 1px 3px rgba(0,0,0,0.06)'
                        : '0 1px 3px rgba(0,0,0,0.12)',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {message.content}
                  </Typography>
                </Box>

                {/* Proposed Actions (outside bubble, below) */}
                {message.role === 'assistant' && message.actions && message.actions.length > 0 && (
                  <Stack spacing={1} mt={1} width="85%">
                    {message.actions.map((action) => (
                      <Box
                        key={`${message.id}-${action.title}`}
                        sx={(theme) => {
                          const colorKey = priorityColorKey(action.priority);

                          return {
                            bgcolor: alpha(theme.palette[colorKey].main, 0.08),
                            border: '1px solid',
                            borderColor: alpha(theme.palette[colorKey].main, 0.18),
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderLeftColor: theme.palette[colorKey].main,
                            px: 1.5,
                            py: 1,
                          };
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip
                            size="small"
                            label={priorityLabelEs(action.priority)}
                            color={priorityColorKey(action.priority)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {action.title}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {action.rationale}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}

            {/* Typing Indicator */}
            {askAssistant.isPending && (
              <Box display="flex" alignItems="center" gap={0.6} pl={0.5}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'grey.400',
                      animation: 'bounce 1.2s ease-in-out infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes bounce': {
                        '0%, 60%, 100%': { transform: 'translateY(0)' },
                        '30%': { transform: 'translateY(-6px)' },
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            {errorMessage && (
              <Typography variant="caption" color="error" px={0.5}>
                {errorMessage}
              </Typography>
            )}

            <div ref={chatBottomRef} />
          </>
        )}
      </Box>

      {/* Footer Input */}
      <Box sx={{ flexShrink: 0 }}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box display="flex" alignItems="flex-end" gap={1}>
            <TextField
              inputRef={textFieldRef}
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu consulta..."
              multiline
              minRows={2}
              maxRows={5}
              disabled={askAssistant.isPending}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={() => sendMessage(draftMessage)}
              disabled={askAssistant.isPending || !draftMessage.trim()}
              sx={{ mb: 0.5 }}
            >
              <SendIcon />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
            Ctrl+Enter para enviar
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};
